/**
 * Утилита для управления SEO meta-тегами
 * Обновляет document.title, meta description, OG-теги, canonical URL и JSON-LD
 */

interface SEOConfig {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  keywords?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
}

const DEFAULT_TITLE = 'Astranum — Персонализированная астрология и нумерология';
const DEFAULT_DESCRIPTION = 'Натальная карта онлайн, расчёт нумерологии по дате рождения, совместимость партнёров, транзиты планет и персональные прогнозы. Точные расчёты Swiss Ephemeris. Бесплатно.';
const BASE_URL = 'https://astranum.ru';
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-astranum.png`;

// ============================================================
// SEO-конфигурации для каждой страницы (подбраны по семантическому ядру)
// ============================================================

export const PAGE_SEO: Record<string, Omit<SEOConfig, 'canonicalUrl'>> = {
  home: {
    title: 'Астрология и нумерология онлайн — натальная карта, совместимость, прогнозы',
    description: 'Рассчитать натальную карту онлайн, нумерология по дате рождения, совместимость партнёров по синастрии, транзиты планет и персональные гороскопы. Точные расчёты Swiss Ephemeris. Бесплатно.',
    keywords: 'натальная карта онлайн, рассчитать натальную карту, нумерология по дате рождения, совместимость по дате рождения, гороскоп персональный, транзиты планет, астрология онлайн, нумерология онлайн, синастрия расчёт, матрица судьбы, пифагоров квадрат, число жизненного пути, элективная астрология, ретроградный меркурий',
  },
  dashboard: {
    title: 'Личный кабинет — моя страница',
    description: 'Ваш персональный дашборд: natal chart, нумерологический профиль, совместимость, прогнозы и аналитика.',
    keywords: 'личный кабинет, дашборд, natal chart, нумерологический профиль',
    noindex: true,
  },
  natal: {
    title: 'Натальная карта онлайн — расчёт с расшифровкой',
    description: 'Рассчитать натальную карту с точными положениями планет, домов и аспектов. Расшифровка на русском языке. Расчёт по Swiss Ephemeris.',
    keywords: 'натальная карта, рассчитать натальную карту, натальная карта онлайн, натальная карта расшифровка, положение планет, дома гороскопа, аспекты планет, ascendant, MC, Swiss Ephemeris',
  },
  numerology: {
    title: 'Нумерология по дате рождения — расчёт чисел онлайн',
    description: 'Рассчитать число жизненного пути, число судьбы, число души и личности. Пифагоров квадрат, матрица судьбы, персональные циклы и благоприятные даты.',
    keywords: 'нумерология по дате рождения, рассчитать нумерологию, число жизненного пути, число судьбы, число души, пифагоров квадрат, матрица судьбы, нумерология онлайн, число имени, персональные циклы',
  },
  forecasts: {
    title: 'Персональные прогнозы и транзиты планет',
    description: 'Персональные астрологические прогнозы: транзиты планет, прогрессии, соляр. Узнайте что ждёт вас в ближайшем будущем.',
    keywords: 'персональный гороскоп, транзиты планет, прогнозы астрология, прогрессии, соляр, астрологический прогноз, транзиты онлайн',
  },
  compatibility: {
    title: 'Совместимость партнёров — синастрия онлайн',
    description: 'Рассчитать совместимость партнёров по натальным картам. Синастрия, композитная карта, анализ отношений. Астрология совместности.',
    keywords: 'совместимость партнёров, синастрия онлайн, совместимость по дате рождения, астрология совместимость, синастрия расчёт, композитная карта, совместимость в отношениях',
  },
  analyses: {
    title: 'Специальные анализы — скрытые таланты натальной карты',
    description: '8 скрытых талантов натальной карты: специальные анализы, которые раскроют ваш потенциал.',
    keywords: 'скрытые таланты натальной карты, специальные анализы, астрологический анализ, таланты по гороскопу',
  },
  electional: {
    title: 'Элективная астрология — выбор благоприятной даты',
    description: 'Выбрать благоприятную дату для важных событий: свадьба, переезд, бизнес. Элективная астрология онлайн.',
    keywords: 'элективная астрология, выбрать благоприятную дату, свадьба дата, бизнес дата, благоприятные даты, элективная карта',
  },
  articles: {
    title: 'Статьи об астрологии и нумерологии — полезные материалы',
    description: 'Полезные статьи о натальной карте, нумерологии, совместимости, транзитах планет. Бесплатные материалы от Astranum.',
    keywords: 'астрология статьи, нумерология статьи, натальная карта чтение, гороскоп статьи, астрология обучение, нумерология обучение',
  },
  profile: {
    title: 'Профиль пользователя',
    description: 'Настройки профиля, фото, подписка, контакты, безопасность, уведомления.',
    noindex: true,
  },
  login: {
    title: 'Вход в Astranum',
    description: 'Войдите в свой аккаунт Astranum для доступа к персональным расчётам и прогнозам.',
    noindex: true,
  },
};

// ============================================================
// Функции
// ============================================================

/**
 * Обновляет все SEO meta-теги на странице
 */
export function setMetaTags(config: SEOConfig): void {
  const {
    title,
    description,
    canonicalUrl,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = 'website',
    keywords,
    noindex = false,
    jsonLd,
  } = config;

  // document.title
  document.title = title === DEFAULT_TITLE ? title : `${title} | Astranum`;

  // meta description
  setOrCreateMeta('name', 'description', description);

  // meta keywords
  if (keywords) {
    setOrCreateMeta('name', 'keywords', keywords);
  }

  // robots (noindex если нужно)
  if (noindex) {
    setOrCreateMeta('name', 'robots', 'noindex, nofollow');
  } else {
    // Удаляем noindex если был
    removeMeta('name', 'robots');
  }

  // canonical link
  if (canonicalUrl) {
    setOrCreateLink('canonical', canonicalUrl);
  }

  // Open Graph теги
  setOrCreateMeta('property', 'og:title', title);
  setOrCreateMeta('property', 'og:description', description);
  setOrCreateMeta('property', 'og:image', ogImage);
  setOrCreateMeta('property', 'og:type', ogType);
  setOrCreateMeta('property', 'og:locale', 'ru_RU');
  setOrCreateMeta('property', 'og:site_name', 'Astranum');
  if (canonicalUrl) {
    setOrCreateMeta('property', 'og:url', canonicalUrl);
  }

  // Twitter Card
  setOrCreateMeta('name', 'twitter:card', 'summary_large_image');
  setOrCreateMeta('name', 'twitter:title', title);
  setOrCreateMeta('name', 'twitter:description', description);
  setOrCreateMeta('name', 'twitter:image', ogImage);

  // JSON-LD (structured data)
  if (jsonLd) {
    setJsonLd(jsonLd);
  }
}

/**
 * Сбрасывает meta-теги к значениям по умолчанию
 */
export function resetMetaTags(): void {
  setMetaTags({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  });
}

/**
 * Удаляет meta-тег
 */
function removeMeta(attributeName: string, attributeValue: string): void {
  const meta = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (meta) {
    meta.remove();
  }
}

/**
 * Устанавливает JSON-LD structured data
 */
function setJsonLd(data: Record<string, unknown>): void {
  let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;

  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

/**
 * Создаёт или обновляет meta-тег
 */
function setOrCreateMeta(attributeName: string, attributeValue: string, content: string): void {
  let meta = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attributeName, attributeValue);
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content);
}

/**
 * Создаёт или обновляет link-тег (canonical)
 */
function setOrCreateLink(rel: string, href: string): void {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
}

/**
 * Генерирует URL страницы из пути
 */
export function buildCanonicalUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

/**
 * Хелпер для генерации OG-image URL из slug статьи
 */
export function buildArticleOgImage(slug: string): string {
  // В будущем можно генерировать динамические OG-изображения
  return DEFAULT_OG_IMAGE;
}

/**
 * Генерирует Article JSON-LD для SEO
 */
export function buildArticleJsonLd(article: {
  title: string;
  description: string;
  slug: string;
  publishedAt?: string;
  author?: string;
  keywords?: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: buildCanonicalUrl(`/articles/${article.slug}`),
    datePublished: article.publishedAt || new Date().toISOString(),
    author: {
      '@type': 'Organization',
      name: article.author || 'Astranum',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Astranum',
      url: BASE_URL,
    },
    keywords: article.keywords,
    mainEntityOfPage: buildCanonicalUrl(`/articles/${article.slug}`),
  };
}

/**
 * Генерирует WebSite JSON-LD для главной страницы
 */
export function buildWebsiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Astranum',
    url: BASE_URL,
    description: DEFAULT_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}/articles?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}
